import React from 'react';
import {inject, observer} from 'mobx-react';
import {mapValues, pick, debounce} from 'lodash';
import {
  ReportCheckPanelWrapper,
  Description,
  ButtonsContainer,
  MainContentWrapper,
  MediaUploaderWrapper,
  Label
} from './styles';

import ModalsTextBox from 'components/ModalsTextBox';
import MediaUploader from 'components/MediaUploader';
import {Button} from '../Button';
import {withSidePanel} from '../SidePanel';
import {EscalateCheck} from '../../api/checklists/mutations';
import {escalateCheckOptions} from '../../api/checklists/mutation-options';
import {injectIntl} from 'react-intl';
import {graphql} from '@apollo/client/react/hoc/graphql';
import messages from './messages';
import {maxFeedbackLength} from './constants';
import InfoModal from 'components/InfoModal';

@injectIntl
@withSidePanel
@graphql(EscalateCheck, escalateCheckOptions)
@inject('store')
@observer
class ReportIssuePanel extends React.Component {
  state = {isDesktop: true};
  resizeListener = null;
  resolveImageRemoval = null;
  rejectImageRemoval = null;

  componentDidMount = () => {
    const {
      intl: {formatMessage},
      store: {
        reportIssue: {setTranslations, reset}
      }
    } = this.props;

    reset();
    const translations = mapValues(
      pick(messages, ['reportedCheck', 'somethingWentWrongPa', 'somethingWentWrong']),
      message => formatMessage(message)
    );
    setTranslations(translations);
    this.resizeListener = window.addEventListener('resize', debounce(this.handleResize, 100));
    this.handleResize();
  };

  componentWillUnmount() {
    const {
      store: {
        reportIssue: {reset}
      }
    } = this.props;
    window.removeEventListener('resize', this.resizeListener);
    reset();
  }

  handleTextareaChange = event => {
    const {
      store: {
        reportIssue: {setFeedbackText}
      }
    } = this.props;
    const feedbackText = event.target.value;

    if (feedbackText.length <= maxFeedbackLength) {
      setFeedbackText(event.target.value);
    }
  };

  handleResize = () => {
    const isDesktop = window.innerWidth >= 768;
    if (isDesktop !== this.state.isDesktop) {
      this.setState({isDesktop});
    }
  };

  handleMediaUploaderUploadStart = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsUploading(true);
  };

  handleMediaUploaderUploadEnd = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsUploading(false);
  };

  handleMediaUploaderBeforeRemove = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    return new Promise((resolve, reject) => {
      this.resolveImageRemoval = resolve;
      this.rejectImageRemoval = reject;
      dialog.setIsRemoveImageDialogOpen(true);
    });
  };

  handleMediaUploaderChange = mediaUrl => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setMediaUrl(mediaUrl);
  };

  handleMediaUploaderError = ({type, data}) => {
    const {intl, store} = this.props;
    const {formatMessage} = intl;
    const {checklist: checklistStore, reportIssue: dialog} = store;

    if (type === 'networkError') {
      dialog.setIsTryAgainErrorDialogOpen(true);
    } else {
      dialog.setMediaUploadErrorText(formatMessage(messages[type] || messages.fileNotSupported, data));
      checklistStore.openErrorModal();
    }
  };

  handleSaveWithoutNewImageDialogClose = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsSaveWithoutNewImageDialogOpen(false);
  };

  handleSaveWithoutNewImageDialogConfirm = async () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsSaveWithoutNewImageDialogOpen(false);
    dialog.setIsUploading(false);
    this.submitIssue();
  };

  handleTryAgainErrorDialogClose = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsTryAgainErrorDialogOpen(false);
  };

  handleTryAgainErrorDialogConfirm = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsTryAgainErrorDialogOpen(false);

    if (this.mediaUploaderRef && this.mediaUploaderRef.wrappedInstance) {
      this.mediaUploaderRef.wrappedInstance.retry();
    }
  };

  handleRemoveImageDialogConfirm = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsRemoveImageDialogOpen(false);

    if (typeof this.resolveImageRemoval === 'function') {
      this.resolveImageRemoval();

      this.resolveImageRemoval = null;
      this.rejectImageRemoval = null;
    }
  };

  handleRemoveImageDialogClose = () => {
    const {store} = this.props;
    const {reportIssue: dialog} = store;

    dialog.setIsRemoveImageDialogOpen(false);

    if (typeof this.rejectImageRemoval === 'function') {
      this.rejectImageRemoval();

      this.resolveImageRemoval = null;
      this.rejectImageRemoval = null;
    }
  };

  openMediaUploaderDialog = () => {
    const {store} = this.props;
    const {checklist: checklistStore} = store;

    if (this.dropzoneRef) {
      this.dropzoneRef.open();
    }

    checklistStore.closeErrorModal();
  };

  setDropzoneRef = ref => {
    this.dropzoneRef = ref;
  };

  submitIssue = async () => {
    const {popSidePanel, escalateCheckMutation, store} = this.props;
    const {
      reportIssue: {escalateCheck, loading}
    } = store;
    if (loading) {
      return;
    }
    await escalateCheck(escalateCheckMutation);
    popSidePanel();
  };

  handleSubmitIssue = async () => {
    const {store} = this.props;
    const {
      reportIssue: {isUploading, setIsSaveWithoutNewImageDialogOpen}
    } = store;
    if (isUploading) {
      setIsSaveWithoutNewImageDialogOpen(true);
      return;
    }
    this.submitIssue();
  };

  render() {
    const {
      store: {
        reportIssue: {
          mediaUrl,
          feedbackText,
          isRemoveImageDialogOpen,
          isSaveWithoutNewImageDialogOpen,
          isTryAgainErrorDialogOpen,
          loading,
          mediaUploadErrorText
        },
        checklist: {showErrorModal, closeErrorModal}
      },
      popSidePanel,
      intl: {formatMessage}
    } = this.props;
    const isDesktop = this.state.isDesktop;

    return (
      <>
        <InfoModal
          isDesktop={isDesktop}
          isOpen={isSaveWithoutNewImageDialogOpen}
          leftButtonText={formatMessage(messages.saveWithoutNewFile)}
          onClose={this.handleSaveWithoutNewImageDialogClose}
          onLeftButtonClick={this.handleSaveWithoutNewImageDialogConfirm}
          onRightButtonClick={this.handleSaveWithoutNewImageDialogClose}
          rightButtonText={formatMessage(messages.cancel)}
          subtitle={formatMessage(messages.fileStillUploadingSubtitle)}
          title={formatMessage(messages.fileStillUploadingTitle)}
        />
        <InfoModal
          isDesktop={isDesktop}
          isOpen={isRemoveImageDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={this.handleRemoveImageDialogClose}
          onLeftButtonClick={this.handleRemoveImageDialogClose}
          onRightButtonClick={this.handleRemoveImageDialogConfirm}
          rightButtonText={formatMessage(messages.delete)}
          subtitle={formatMessage(messages.removeFileSubtitle)}
          title={formatMessage(messages.removeFileTitle)}
        />
        <InfoModal
          isDesktop={isDesktop}
          isOpen={showErrorModal}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={closeErrorModal}
          onLeftButtonClick={closeErrorModal}
          onRightButtonClick={this.openMediaUploaderDialog}
          rightButtonText={formatMessage(messages.uploadNew)}
          subtitle={mediaUploadErrorText}
          title={formatMessage(messages.couldNotUploadFileTitle)}
        />
        <InfoModal
          isDesktop={isDesktop}
          isOpen={isTryAgainErrorDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={this.handleTryAgainErrorDialogClose}
          onLeftButtonClick={this.handleTryAgainErrorDialogClose}
          onRightButtonClick={this.handleTryAgainErrorDialogConfirm}
          rightButtonText={formatMessage(messages.tryAgain)}
          subtitle={formatMessage(messages.couldNotUploadFileSubtitle)}
          title={formatMessage(messages.couldNotUploadFileTitle)}
        />
        <InfoModal
          isDesktop={isDesktop}
          isOpen={isRemoveImageDialogOpen}
          leftButtonText={formatMessage(messages.cancel)}
          onClose={this.handleRemoveImageDialogClose}
          onLeftButtonClick={this.handleRemoveImageDialogClose}
          onRightButtonClick={this.handleRemoveImageDialogConfirm}
          rightButtonText={formatMessage(messages.delete)}
          subtitle={formatMessage(messages.removeFileSubtitle)}
          title={formatMessage(messages.removeFileTitle)}
        />

        <ReportCheckPanelWrapper>
          <MainContentWrapper>
            <Description>{formatMessage(messages.reportIssueDescription)}</Description>

            <Label>{formatMessage(messages.details)}</Label>
            <ModalsTextBox
              dataCy={'report-issue-text'}
              isDesktop={isDesktop}
              handleTextareaChange={this.handleTextareaChange}
              placeholder={formatMessage(messages.describeIssue)}
              value={feedbackText}
              maxFeedbackLength={maxFeedbackLength}
            />
            <MediaUploaderWrapper isDesktop={isDesktop}>
              <MediaUploader
                isDesktop={isDesktop}
                onUploadStart={this.handleMediaUploaderUploadStart}
                onUploadEnd={this.handleMediaUploaderUploadEnd}
                onBeforeRemove={this.handleMediaUploaderBeforeRemove}
                onChange={this.handleMediaUploaderChange}
                onError={this.handleMediaUploaderError}
                value={mediaUrl}
                setDropzoneRef={this.setDropzoneRef}
              />
            </MediaUploaderWrapper>
          </MainContentWrapper>
          <ButtonsContainer>
            <Button aria-label="report" onClick={this.handleSubmitIssue} loading={loading}>
              {formatMessage(messages.submitIssue)}
            </Button>
            <Button
              aria-label="cancel"
              design="outline"
              onClick={() => {
                popSidePanel();
              }}
            >
              {formatMessage(messages.cancel)}
            </Button>
          </ButtonsContainer>
        </ReportCheckPanelWrapper>
      </>
    );
  }
}

export default ReportIssuePanel;
